<template>
  <div class="flowbuilder-settings-menu" v-if="shouldShowMenu">
    <!-- Settings trigger icon -->
    <div class="absolute right-1.5 top-1.5 opacity-50 transition-opacity hover:opacity-100 cursor-pointer z-10"
      @click.prevent.stop="toggleMenu" ref="triggerEl">
      <component :is="settingsIcon" class="h-5 w-5" />
    </div>

    <!-- Dropdown menu -->
    <Teleport to="body">
      <div v-if="isOpen"
        class="fixed z-50 min-w-[12.5rem] rounded-md shadow-md p-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-100 border border-gray-200 dark:border-gray-700"
        :style="{ left: menuPosition.x + 'px', top: menuPosition.y + 'px' }" @click.stop>
        <div class="flex flex-col list-none m-0 p-0 outline-none">
          <!-- Custom menu items -->
          <template v-for="(item, index) in items" :key="index">
            <div v-if="item.separator" class="border-t border-gray-200 dark:border-gray-600 my-1" />
            <div v-else class="relative my-[2px] first:mt-0">
              <button type="button" :class="[
                'relative flex items-center py-2 px-3 w-full rounded-[4px] no-underline overflow-hidden cursor-pointer select-none transition-shadow duration-200 text-sm',
                item.class || 'text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
              ]" @click="handleItemClick(item)">
                <component
                  :is="resolveFlowIcon(item.icon)"
                  v-if="item.icon"
                  :class="['mr-2 h-4 w-4', item.class ? '' : 'text-gray-600 dark:text-gray-400']"
                />
                <span class="leading-none">{{ item.label }}</span>
              </button>
            </div>
          </template>

          <!-- Built-in Duplicate option -->
          <div v-if="!hideDuplicate && canAdd" class="relative my-[2px]">
            <button type="button"
              class="relative flex items-center py-2 px-3 w-full rounded-[4px] no-underline overflow-hidden cursor-pointer select-none transition-shadow duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-100"
              @click="handleDuplicate">
              <component :is="resolvedDuplicateIcon" class="mr-2 h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span class="leading-none">Duplicate</span>
            </button>
          </div>

          <!-- Separator before Remove -->
          <div v-if="items.length > 0 || (!hideDuplicate && canAdd)" class="border-t border-gray-200 dark:border-gray-600 my-1" />

          <!-- Default Remove option -->
          <div class="relative my-[2px]">
            <button type="button"
              class="relative flex items-center py-2 px-3 w-full rounded-[4px] no-underline overflow-hidden cursor-pointer select-none transition-shadow duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
              @click="handleRemove">
              <component :is="trashIcon" class="mr-2 h-4 w-4 text-red-600 dark:text-red-400" />
              <span class="leading-none text-red-600 dark:text-red-400">{{ deleteLabel }}</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Click outside handler -->
    <div v-if="isOpen" class="fixed inset-0 z-40" @click.prevent.stop="closeMenu" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, unref, onMounted, onUnmounted, type Ref } from 'vue'
import { DocumentDuplicateIcon, resolveFlowIcon } from '../icons'

interface MenuItem {
  label: string
  icon?: any
  command?: () => void
  separator?: boolean
  class?: string
}

interface Props {
  items?: MenuItem[]
  hideMenu?: boolean
  hideDuplicate?: boolean
  settingsIcon?: any
  trashIcon?: any
  duplicateIcon?: any
  deleteLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  hideMenu: false,
  hideDuplicate: false,
  deleteLabel: 'Remove'
})

const emit = defineEmits(['delete-node', 'clone-node', 'menu-opened', 'menu-closed'])

// Inject preview states
const preview = inject('preview', false)
const templatePreview = inject('templatePreview', false)
const canAddRef = inject<Ref<boolean>>('canAdd', ref(true))
const canAdd = computed(() => unref(canAddRef))

// Menu state
const isOpen = ref(false)
const triggerEl = ref<HTMLElement | null>(null)
const menuPosition = ref({ x: 0, y: 0 })
const justClosed = ref(false)

// Don't show menu in preview modes or when canAdd is false
const shouldShowMenu = computed(() => {
  return !preview && !templatePreview && canAdd.value && !props.hideMenu
})

const toggleMenu = (event: MouseEvent) => {
  event.stopImmediatePropagation()

  if (isOpen.value) {
    closeMenu()
  } else {
    openMenu(event)
  }
}

const openMenu = (event?: MouseEvent) => {
  // Use event target if available, otherwise fall back to triggerEl
  const targetEl = event?.currentTarget as HTMLElement || triggerEl.value
  if (!targetEl) return

  const rect = targetEl.getBoundingClientRect()
  const menuWidth = 160
  const menuHeight = 200 // Approximate height

  // Calculate position to avoid viewport edges
  let x = rect.right - menuWidth
  let y = rect.bottom + 4

  // Adjust if menu would go off screen
  if (x < 8) x = 8
  if (x + menuWidth > window.innerWidth - 8) {
    x = window.innerWidth - menuWidth - 8
  }

  if (y + menuHeight > window.innerHeight - 8) {
    y = rect.top - menuHeight - 4
  }

  menuPosition.value = { x, y }
  isOpen.value = true
  emit('menu-opened')
}

const closeMenu = () => {
  isOpen.value = false
  justClosed.value = true
  emit('menu-closed')

  // Reset the justClosed flag after a short delay
  setTimeout(() => {
    justClosed.value = false
  }, 100)
}

const handleItemClick = (item: MenuItem) => {
  if (item.command) {
    item.command()
  }
  closeMenu()
}

const handleDuplicate = () => {
  emit('clone-node')
  closeMenu()
}

const resolvedDuplicateIcon = computed(() => props.duplicateIcon || DocumentDuplicateIcon)

const handleRemove = () => {
  emit('delete-node')
  closeMenu()
}

// Close on escape key
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isOpen.value) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>